import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { Upload } from 'antd'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  DividendPreviewUploadKey,
  UploadDividendPaymentDataDto,
} from 'api/Trading-Accounts/Types/DividendsDto'
import axios from 'axios'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import { errorToast, infoToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { formatLocalDateUTC } from 'utils/date'
import { hasPermission } from 'utils/permissionsValidator'

interface Props {
  onClose(): void
  onSubmit(): void
  permissions: PermissionDto[]
}

export const UploadDividendModal: React.FC<Props> = (props: Props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [dividendFilePreview, setDividendFilePreview] = useState<UploadDividendPaymentDataDto>()
  const [fileInfo, setFileInfo] = useState<File>()
  const setToast = useContext(ToastContext)
  const handleUploadFile = async () => {
    try {
      if (fileInfo) {
        const formData = new FormData()
        formData.append('file', fileInfo)
        const response = await tradingAccountsApiClient.uploadDividendPreview(formData)
        setDividendFilePreview(response)
      }
    } catch (e: unknown) {
      console.error(e)
      if (axios.isAxiosError(e)) {
        const err = e.response?.data?.message
        if (err) {
          setToast(errorToast(`Error: ${err}`))
        } else {
          setToast(errorToast('Error uploading dividend file.'))
        }
      } else {
        setToast(errorToast('An unexpected error occurred.'))
      }
    }
  }

  const handleSelectFile = (file: File) => {
    if (file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setToast(infoToast('Supported format is only csv.'))
      return false
    }
    setFileInfo(file)
    return false
  }

  const handleConfirmationSubmit = async () => {
    if (
      !hasPermission(props.permissions, PermissionsEnum.CanUploadCsvFilesToCreateDividendPayments)
    )
      return setToast(errorToast('Permission denied'))
    if (fileInfo) {
      try {
        setToast(infoToast('Uploading dividend file ...'))
        const formData = new FormData()
        formData.append('file', fileInfo)
        await tradingAccountsApiClient.uploadDividendFile(formData)
        setToast(successToast('Dividend has been uploaded.'))
        props.onClose()
        props.onSubmit()
      } catch (error: unknown) {
        console.error(error)
        setToast(errorToast('Error uploading dividend file.'))
      }
    }
  }

  return (
    <Modal width={1200} visible={true} onClose={props.onClose}>
      <Upload beforeUpload={handleSelectFile} showUploadList={false}>
        <Button type='button'>{fileInfo?.name ? fileInfo?.name : <>Select File ...</>}</Button>
      </Upload>
      <Button type='button' onClick={handleUploadFile}>
        Upload
      </Button>
      <Grid data={dividendFilePreview?.items} editField='inEdit' resizable>
        <Column
          field={DividendPreviewUploadKey.brokerSymbol}
          title='Broker Symbol'
          filter='text'
          minResizableWidth={150}
          width={150}
        />
        <Column
          field={DividendPreviewUploadKey.eventType}
          title='Event Type'
          minResizableWidth={200}
          width={200}
        />
        <Column
          field={DividendPreviewUploadKey.eventSubType}
          title='Event SubType'
          minResizableWidth={200}
          width={200}
        />
        <Column
          field={DividendPreviewUploadKey.eventDate}
          title='Event Date'
          minResizableWidth={200}
          width={200}
          cell={(gridCellProps: GridCellProps) => (
            <td className={'table-columns__number'}>
              {formatLocalDateUTC(gridCellProps.dataItem.dividendPaymentData.eventDate, false)}
            </td>
          )}
        />
        <Column
          field={DividendPreviewUploadKey.amount}
          title='Amount'
          minResizableWidth={100}
          width={100}
        />
        <Column
          field={DividendPreviewUploadKey.currency}
          title='Currency'
          minResizableWidth={100}
          width={100}
        />
        <Column
          field={DividendPreviewUploadKey.comment}
          title='Comment'
          minResizableWidth={150}
          width={150}
        />
      </Grid>
      <FlexBox style={{ marginTop: 48, marginBottom: 48 }} alignItems='left' justifyContent='left'>
        <Button
          disabled={!dividendFilePreview}
          type='button'
          onClick={async () => await handleConfirmationSubmit()}
        >
          Submit
        </Button>
      </FlexBox>
    </Modal>
  )
}
